import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'reactstrap';
import { APIClient } from '../../helpers/api_helper';
import { usePermissions } from '../Hooks/PermissionHooks';
import ClockErrorModal from './ClockErrorModal';
import webSocketService from '../../services/websocket.service';
import moment from 'moment';
import 'moment/locale/de';

moment.locale('de');

const api = new APIClient();

interface ClockData {
  id: number;
  clock_in: string;
  current_minutes: number;
}

const ClockDisplay: React.FC = () => {
  const navigate = useNavigate();
  const { user } = usePermissions();
  const [clockData, setClockData] = useState<ClockData | null>(null);
  const [currentTime, setCurrentTime] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [clockStartTime, setClockStartTime] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorDetails, setErrorDetails] = useState<any>({});
  const [isPaused, setIsPaused] = useState(false);
  const [isProcessingPause, setIsProcessingPause] = useState(false);
  const [workSecondsAtPauseStart, setWorkSecondsAtPauseStart] = useState(0);
  const [pauseStartTime, setPauseStartTime] = useState<Date | null>(null);
  const [totalPauseSeconds, setTotalPauseSeconds] = useState(0);
  const [hasInitializedPauses, setHasInitializedPauses] = useState(false);

  const fetchClockStatus = async () => {
    try {
      // Check auth token
      const authUser = sessionStorage.getItem('authUser');

      if (!authUser) {
        setError('Not authenticated');
        setIsLoading(false);
        return;
      }

      const response = await api.get('/clock/status');

      if (response.data.success && response.data.data) {
        setClockData(response.data.data);
        setError(null);
      } else {
        setClockData(null);
        setError(null);
      }
    } catch (error: any) {
      console.error('Error fetching clock status:', error);
      setError(error?.response?.data?.error || error?.message || 'Failed to fetch clock status');
      setClockData(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchClockStatus();

    // Listen for clock events
    const handleClockEvent = () => {
      fetchClockStatus();
    };

    window.addEventListener('clockStarted', handleClockEvent);
    window.addEventListener('clockStopped', handleClockEvent);

    return () => {
      window.removeEventListener('clockStarted', handleClockEvent);
      window.removeEventListener('clockStopped', handleClockEvent);
    };
  }, []);

  useEffect(() => {
    if (!clockData) {
      setCurrentTime('');
      setClockStartTime('');
      setIsPaused(false);
      return;
    }

    // Only check pause status once on initial load
    if (!hasInitializedPauses) {
      checkPauseStatus();
      setHasInitializedPauses(true);
    }

    // Set the start time
    const startMoment = moment(clockData.clock_in);
    setClockStartTime(startMoment.format('HH:mm'));

    const updateTime = () => {
      if (isPaused) {
        // When paused, just show the frozen work time
        const hours = Math.floor(workSecondsAtPauseStart / 3600);
        const minutes = Math.floor((workSecondsAtPauseStart % 3600) / 60);
        const seconds = Math.floor(workSecondsAtPauseStart % 60);
        const timeString = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
        setCurrentTime(timeString);
      } else {
        // When not paused, calculate actual working time
        const start = moment(clockData.clock_in);
        const now = moment();
        const totalElapsedSeconds = moment.duration(now.diff(start)).asSeconds();

        // Working seconds = total elapsed - all pause time
        const workingSeconds = Math.max(0, totalElapsedSeconds - totalPauseSeconds);

        const hours = Math.floor(workingSeconds / 3600);
        const minutes = Math.floor((workingSeconds % 3600) / 60);
        const seconds = Math.floor(workingSeconds % 60);

        const timeString = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
        setCurrentTime(timeString);
      }
    };

    updateTime();

    // Only run interval when NOT paused
    if (!isPaused) {
      const timer = setInterval(updateTime, 1000);
      return () => clearInterval(timer);
    }
  }, [clockData?.id, isPaused, workSecondsAtPauseStart, totalPauseSeconds]);

  const handleStopClock = async () => {
    if (!clockData) return;
    
    // Calculate duration for confirmation message
    const duration = moment.duration(moment().diff(moment(clockData.clock_in)));
    const hours = Math.floor(duration.asHours());
    const minutes = duration.minutes();
    const timeString = `${hours} Stunde${hours !== 1 ? 'n' : ''} und ${minutes} Minute${minutes !== 1 ? 'n' : ''}`;
    
    // Show confirmation dialog
    const confirmMessage = user?.no_timeentries === 1 
      ? `Möchtest du die Clock nach ${timeString} beenden?`
      : `Möchtest du die Clock nach ${timeString} beenden und in Zeiteinträge umwandeln?`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }
    
    try {
      const response = await api.create('/clock/stop', {});
      if (response.data.success) {
        // Dispatch custom event for other components
        window.dispatchEvent(new Event('clockStopped'));
        
        // Check if clock was cancelled due to less than 15 minutes
        if (response.data.wasCancelled) {
          await fetchClockStatus();
          alert(response.data.message);
          return;
        }
        
        // Check if user has no_timeentries flag
        if (user?.no_timeentries === 1) {
          // Just refresh the clock status - don't navigate
          await fetchClockStatus();
          alert('Clock wurde erfolgreich beendet.');
        } else {
          // Navigate to convert page with the clock data
          navigate('/time-entries/convert-clock', { 
            state: { clockData: response.data.data } 
          });
        }
      }
    } catch (error) {
      console.error('Error stopping clock:', error);
      alert('Fehler beim Beenden der Clock.');
    }
  };

  const handleTogglePause = async () => {
    if (!clockData || isProcessingPause) return;

    setIsProcessingPause(true);
    try {
      const response = await api.create('/clock/pause/toggle', {});
      if (response.data.success) {
        if (response.data.data.paused) {
          // PAUSE STARTED
          // Calculate and freeze current work seconds
          const start = moment(clockData.clock_in);
          const now = moment();
          const totalElapsedSeconds = moment.duration(now.diff(start)).asSeconds();
          const currentWorkSeconds = Math.max(0, totalElapsedSeconds - totalPauseSeconds);

          setWorkSecondsAtPauseStart(currentWorkSeconds);
          setIsPaused(true);
          setPauseStartTime(new Date());

          // WebSocket event is emitted by backend
        } else {
          // PAUSE ENDED
          // Fetch updated pause history from backend to get exact total pause time
          await checkPauseStatus(true); // skipActiveCheck = true to avoid race condition

          setIsPaused(false);
          setPauseStartTime(null);
          setWorkSecondsAtPauseStart(0);

          // WebSocket event is emitted by backend
        }
      }
    } catch (error: any) {
      console.error('Error toggling pause:', error);
      alert(error.response?.data?.error || 'Fehler beim Pausieren');
    } finally {
      setIsProcessingPause(false);
    }
  };

  const checkPauseStatus = async (skipActiveCheck = false) => {
    if (clockData?.id) {
      try {
        const response = await api.get(`/clock/pause/history/${clockData.id}`);
        if (response.data.success) {
          // Calculate total pause time from ALL pauses (active and completed)
          const allPauses = response.data.data.pauses || [];
          let totalMinutes = 0;

          for (const pause of allPauses) {
            if (pause.duration_minutes) {
              // Completed pause
              totalMinutes += pause.duration_minutes;
            }
          }

          // ALWAYS update totalPauseSeconds when we fetch pause history
          setTotalPauseSeconds(totalMinutes * 60);

          // Only update isPaused if we're not in the middle of toggling
          if (!skipActiveCheck) {
            const activePause = allPauses.find((p: any) => p.is_active);
            if (activePause && clockData) {
              // Calculate work seconds at the time the pause started
              const start = moment(clockData.clock_in);
              const pauseStart = moment(activePause.pause_start);
              const secondsUntilPause = moment.duration(pauseStart.diff(start)).asSeconds();

              // Work seconds = time until pause - previous pauses
              const workSeconds = Math.max(0, secondsUntilPause - totalMinutes * 60);
              setWorkSecondsAtPauseStart(workSeconds);

              setIsPaused(true);
              setPauseStartTime(new Date(activePause.pause_start));
            } else {
              setIsPaused(false);
              setPauseStartTime(null);
              setWorkSecondsAtPauseStart(0);
            }
          }
        }
      } catch (error) {
        console.error('Error checking pause status:', error);
      }
    }
  };

  // Listen for clock and pause events via WebSocket
  useEffect(() => {
    const handleClockStarted = (data: any) => {
      console.log('Clock started WebSocket event received in header:', data, 'current user:', user?.id);
      // Update if it's for the current user
      // Also fetch if we don't have a user yet (initial load)
      if (!user || user.id === data.userId) {
        fetchClockStatus();
      }
    };

    const handleClockStopped = (data: any) => {
      console.log('Clock stopped WebSocket event received in header:', data, 'current user:', user?.id);
      // Update if it's for the current user
      if (!user || user.id === data.userId) {
        fetchClockStatus();
      }
    };

    const handlePauseStarted = (data: any) => {
      console.log('Pause started WebSocket event received in header:', data);
      // Update if it's for the current user (check both userId and clockId)
      if (user?.id === data.userId || clockData?.id === data.clockId) {
        checkPauseStatus();
      }
    };

    const handlePauseEnded = (data: any) => {
      console.log('Pause ended WebSocket event received in header:', data);
      // Update if it's for the current user (check both userId and clockId)
      if (user?.id === data.userId || clockData?.id === data.clockId) {
        checkPauseStatus();
      }
    };

    webSocketService.onClockStarted(handleClockStarted);
    webSocketService.onClockStopped(handleClockStopped);
    webSocketService.onPauseStarted(handlePauseStarted);
    webSocketService.onPauseEnded(handlePauseEnded);

    return () => {
      webSocketService.offClockStarted(handleClockStarted);
      webSocketService.offClockStopped(handleClockStopped);
      webSocketService.offPauseStarted(handlePauseStarted);
      webSocketService.offPauseEnded(handlePauseEnded);
    };
  }, [clockData?.id, user?.id]);

  const handleStartClock = async () => {
    try {
      const response = await api.create('/clock/start', {});
      if (response.data.success) {
        await fetchClockStatus();

        // Dispatch custom event for other components
        window.dispatchEvent(new Event('clockStarted'));
      }
    } catch (error: any) {
      console.error('Error starting clock:', error);

      // Show error modal with details
      if (error.response?.data) {
        setErrorDetails(error.response.data);
        setShowErrorModal(true);
      } else {
        // Fallback error handling
        setErrorDetails({
          error: 'Fehler beim Starten der Clock',
          details: error.message || 'Ein unbekannter Fehler ist aufgetreten'
        });
        setShowErrorModal(true);
      }
    }
  };

  if (isLoading) {
    return (
      <div className="ms-1 header-item d-flex">
        <div className="text-muted px-2">⏰ Loading...</div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="ms-1 header-item d-flex">
        <div className="text-danger px-2" title={error}>⚠️ Clock Error</div>
      </div>
    );
  }
  
  if (!clockData) {
    return (
      <div className="ms-1 header-item d-none d-sm-flex">
        <button
          type="button"
          className="btn btn-icon btn-topbar btn-ghost-secondary rounded-circle"
          onClick={handleStartClock}
          title="Clock starten"
        >
          <i className="ri-time-line fs-22"></i>
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="ms-1 header-item d-flex">
        <div className="d-flex align-items-center gap-2">
          <Button
            color="soft-success"
            className="btn-icon position-relative"
            onClick={() => navigate('/clock/history')}
            title={`Clock läuft seit ${clockStartTime} Uhr`}
          >
            <i className="ri-time-line fs-4"></i>
            <span className="position-absolute top-0 start-100 translate-middle p-1 bg-success border border-light rounded-circle">
              <span className="visually-hidden">Clock läuft</span>
            </span>
          </Button>
          <div
            className={`${isPaused ? 'bg-soft-warning text-warning' : 'bg-soft-success text-success'} px-2 py-1 rounded d-flex align-items-center`}
            style={{ minWidth: '80px' }}
          >
            <span className="fw-bold">{currentTime}</span>
          </div>
          <Button
            color={isPaused ? "success" : "warning"}
            size="sm"
            className="btn-icon"
            onClick={(e) => {
              e.stopPropagation();
              handleTogglePause();
            }}
            disabled={isProcessingPause}
            title={isPaused ? "Pause beenden" : "Pause starten"}
          >
            <i className={isPaused ? "ri-play-circle-line" : "ri-pause-circle-line"}></i>
          </Button>
          <Button
            color="danger"
            size="sm"
            className="btn-icon"
            onClick={(e) => {
              e.stopPropagation();
              handleStopClock();
            }}
            title="Clock beenden"
          >
            <i className="ri-stop-circle-line"></i>
          </Button>
        </div>
      </div>
      
      {/* Error Modal */}
      <ClockErrorModal
        isOpen={showErrorModal}
        toggle={() => setShowErrorModal(false)}
        error={errorDetails.error}
        details={errorDetails.details}
        unconvertedClock={errorDetails.unconvertedClock}
        manualEntriesExist={errorDetails.manualEntriesExist}
        manualMinutes={errorDetails.manualMinutes}
        clockMinutes={errorDetails.clockMinutes}
        difference={errorDetails.difference}
        canAutoConvert={errorDetails.canAutoConvert}
      />
    </>
  );
};

export default ClockDisplay;